/**
 * Global VoIP context — instantiates the VoIP device hook once at the
 * app layout level so incoming calls are handled regardless of which
 * page the agent is on.
 *
 * Renders:
 * - IncomingCallOverlay when an inbound call is ringing
 * - ActiveCallBar when any call (inbound or outbound) is in progress
 * - CallNotesPanel slide-out when open
 * - beforeunload warning to prevent accidental refresh during a call
 */

import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useVoIPDevice } from '../hooks/useVoIPDevice'
import type { DeviceStatus, CallOutcome, IncomingCallInfo } from '../hooks/useVoIPDevice'
import IncomingCallOverlay from '../components/voip/IncomingCallOverlay'
import ActiveCallBar from '../components/voip/ActiveCallBar'
import CallNotesPanel from '../components/voip/CallNotesPanel'
import PostCallModal from '../components/voip/PostCallModal'
import { voipApi } from '../api/endpoints'
import { useNotificationStore } from '../stores/notifications'

/** Metadata about the active call — set by VoIPPage (outbound) or looked up (inbound). */
interface ActiveCallMeta {
  callId: number
  leadId: number | null
  leadName: string | null
}

/** Post-call context for unknown caller lead creation prompt. */
interface PostCallContext {
  voipCallId: number
  callerNumber: string
}

interface VoIPContextValue {
  status: DeviceStatus
  error: string | null
  provider: string | null
  callOutcome: CallOutcome
  connect: (phoneNumber: string) => void
  disconnect: () => void
  toggleMute: () => void
  acceptIncoming: () => void
  rejectIncoming: () => void
  isMuted: boolean
  duration: number
  hasWebRTC: boolean
  incomingCall: IncomingCallInfo | null
  activeCallNumber: string | null
  // Minimize
  isCallBarMinimized: boolean
  toggleCallBarMinimize: () => void
  // Notes panel
  isNotesPanelOpen: boolean
  openNotesPanel: () => void
  closeNotesPanel: () => void
  // Active call metadata
  activeCallMeta: ActiveCallMeta | null
  setActiveCallMeta: (meta: ActiveCallMeta | null) => void
  // Post-call unknown caller prompt
  postCallContext: PostCallContext | null
  clearPostCallContext: () => void
}

const VoIPContext = createContext<VoIPContextValue | null>(null)

export function VoIPProvider({ children }: { children: React.ReactNode }) {
  const device = useVoIPDevice()
  const navigate = useNavigate()
  const [isCallBarMinimized, setIsCallBarMinimized] = useState(false)
  const [isNotesPanelOpen, setIsNotesPanelOpen] = useState(false)
  const [activeCallMeta, setActiveCallMeta] = useState<ActiveCallMeta | null>(null)
  const [postCallContext, setPostCallContext] = useState<PostCallContext | null>(null)
  const [showPostCallModal, setShowPostCallModal] = useState(false)

  // Track whether the current/recent call was inbound (for post-call prompt)
  const wasInboundRef = useRef(false)
  const callerNumberRef = useRef<string | null>(null)

  // Multi-tab coordination via BroadcastChannel
  const channelRef = useRef<BroadcastChannel | null>(null)
  const [inboundHandledByOtherTab, setInboundHandledByOtherTab] = useState(false)

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const ch = new BroadcastChannel('opsnest-voip')
    channelRef.current = ch

    ch.onmessage = (e) => {
      const msg = e.data as { type: string }
      if (msg.type === 'inbound-accepted' || msg.type === 'inbound-rejected') {
        // Another tab handled the inbound call — dismiss our overlay
        setInboundHandledByOtherTab(true)
        // Reset after a short delay so the next inbound call works
        setTimeout(() => setInboundHandledByOtherTab(false), 2000)
      }
    }

    return () => { ch.close(); channelRef.current = null }
  }, [])

  const isOnCall = device.status === 'on-call' || device.status === 'connecting' || device.status === 'ringing-inbound'

  // Fire browser notification for inbound calls — uses Notification API directly
  // to avoid hook dependencies that cause re-renders during call setup
  useEffect(() => {
    if (device.status !== 'ringing-inbound' || !device.incomingCall) return
    try {
      const { permission, prefs } = useNotificationStore.getState()
      if (permission !== 'granted' || !prefs.inbound_call) return
      const callerInfo = device.incomingCall.callerName !== device.incomingCall.callerNumber
        ? `${device.incomingCall.callerName}\n${device.incomingCall.callerNumber}`
        : device.incomingCall.callerNumber
      const n = new Notification('Incoming Call', {
        body: callerInfo,
        icon: '/favicon.ico',
        tag: 'inbound_call',
      })
      n.onclick = () => { window.focus(); n.close() }
    } catch { /* ignore — notifications are non-critical */ }
  }, [device.status, device.incomingCall])

  // Track inbound calls
  useEffect(() => {
    if (device.status === 'ringing-inbound' && device.incomingCall) {
      wasInboundRef.current = true
      callerNumberRef.current = device.incomingCall.callerNumber
    }
  }, [device.status, device.incomingCall])

  // Auto-restore bar when a new call starts (don't stay minimized across calls)
  useEffect(() => {
    if (isOnCall) return
    setIsCallBarMinimized(false)
  }, [isOnCall])

  // When an inbound call is accepted and transitions to on-call, look up the call ID
  // and navigate to the Calls page (single source of truth for call flows)
  const prevStatusRef = useRef(device.status)
  useEffect(() => {
    const wasRinging = prevStatusRef.current === 'ringing-inbound' || prevStatusRef.current === 'connecting'
    if (device.status === 'on-call' && wasRinging && wasInboundRef.current) {
      // Look up the most recent inbound call to get its ID.
      // Try in_progress first, then completed (short calls may finish before lookup).
      const findInboundCall = async () => {
        for (const s of ['in_progress', 'completed']) {
          try {
            const res = await voipApi.list({ limit: '3', status: s })
            const call = (res.calls || []).find((c: any) => c.direction === 'inbound')
            if (call) return call
          } catch { /* ignore */ }
        }
        return null
      }
      findInboundCall().then((call) => {
        if (call) {
          setActiveCallMeta({
            callId: call.id,
            leadId: call.lead_id,
            leadName: call.lead_name,
          })
          // Navigate to Calls page if not already there
          if (!window.location.pathname.startsWith('/voip')) {
            navigate(call.lead_id ? `/voip?lead=${call.lead_id}` : '/voip')
          }
        }
      })
    }
    prevStatusRef.current = device.status
  }, [device.status])

  // Global call outcome sync — single source of truth for updating backend
  useEffect(() => {
    if (device.callOutcome === 'none') return
    const callId = activeCallMeta?.callId
    if (callId) {
      voipApi.updateStatus(callId, {
        status: device.callOutcome,
        duration_seconds: device.duration > 0 ? device.duration : undefined,
      }).catch(() => {})
    }

    if (device.callOutcome !== 'connected') {
      cleanupAfterCall()
      return
    }

    // For unknown inbound calls, show lead creation prompt
    if (wasInboundRef.current && (!activeCallMeta || !activeCallMeta.leadId)) {
      // If activeCallMeta is null (lookup didn't complete), do a quick lookup now
      const callerNum = callerNumberRef.current || device.activeCallNumber || 'Unknown'
      if (activeCallMeta?.callId) {
        setPostCallContext({ voipCallId: activeCallMeta.callId, callerNumber: callerNum })
      } else {
        // Fallback: find the call by looking up recent completed inbound calls
        voipApi.list({ limit: '3', status: 'completed' }).then((res) => {
          const call = (res.calls || []).find((c: any) => c.direction === 'inbound' && !c.lead_id)
          if (call) {
            setActiveCallMeta({ callId: call.id, leadId: null, leadName: null })
            setPostCallContext({ voipCallId: call.id, callerNumber: callerNum })
            if (!window.location.pathname.startsWith('/voip')) {
              setShowPostCallModal(true)
            }
          }
        }).catch(() => {})
      }
    }

    // Show modal if not on VoIP page
    if (!window.location.pathname.startsWith('/voip') && activeCallMeta?.callId) {
      setShowPostCallModal(true)
    }
    cleanupAfterCall()
  }, [device.callOutcome])

  function cleanupAfterCall() {
    // Save notes to lead before closing the panel (if panel was open and call has a lead)
    if (isNotesPanelOpen && activeCallMeta?.callId) {
      voipApi.saveNotesToLead(activeCallMeta.callId).catch(() => {})
    }
    setIsNotesPanelOpen(false)
    // Don't clear activeCallMeta immediately — post-call rating needs it
    // It gets cleared when a new call starts
    wasInboundRef.current = false
  }

  // Clear stale state when a new call starts (outbound or inbound)
  useEffect(() => {
    if (device.status === 'connecting' || device.status === 'ringing-inbound') {
      // Dismiss any post-call modal/prompt so it doesn't compete with the new call
      setShowPostCallModal(false)
      setPostCallContext(null)
    }
    if (device.status === 'connecting') {
      setActiveCallMeta(null)
      wasInboundRef.current = false
    }
  }, [device.status])

  // Warn before refresh/close when on a call + notify backend via sendBeacon
  const activeCallMetaRef = useRef(activeCallMeta)
  activeCallMetaRef.current = activeCallMeta
  const durationRef = useRef(device.duration)
  durationRef.current = device.duration

  useEffect(() => {
    if (!isOnCall) return

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = 'You are currently on a call. Leaving will disconnect the call.'

      // Best-effort: notify backend that the call ended (browser is closing)
      const callId = activeCallMetaRef.current?.callId
      if (callId) {
        const payload = JSON.stringify({
          status: 'not_connected',
          duration_seconds: durationRef.current > 0 ? durationRef.current : undefined,
        })
        navigator.sendBeacon(`/api/voip/call/${callId}/status`, new Blob([payload], { type: 'application/json' }))
      }

      return e.returnValue
    }

    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isOnCall])

  // On mount: clean up stale calls from previous sessions (e.g., browser crash).
  // Delay 5s to avoid killing an active call during navigation/re-mount.
  const deviceStatusRef = useRef(device.status)
  deviceStatusRef.current = device.status
  const cleanupRanRef = useRef(false)

  useEffect(() => {
    if (cleanupRanRef.current) return
    cleanupRanRef.current = true
    const timer = setTimeout(() => {
      const currentStatus = deviceStatusRef.current
      if (currentStatus === 'on-call' || currentStatus === 'connecting' || currentStatus === 'ringing-inbound') {
        return // Active call — don't clean up
      }
      voipApi.list({ limit: '5', status: 'in_progress' }).then((res) => {
        if (!res.calls?.length) return
        // Double-check status hasn't changed during the async call
        const nowStatus = deviceStatusRef.current
        if (nowStatus === 'on-call' || nowStatus === 'connecting' || nowStatus === 'ringing-inbound') return
        for (const call of res.calls) {
          voipApi.updateStatus(call.id, { status: 'not_connected' }).catch(() => {})
        }
      }).catch(() => {})
    }, 5000)
    return () => clearTimeout(timer)
  }, [])

  const openNotesPanel = useCallback(() => setIsNotesPanelOpen(true), [])
  const closeNotesPanel = useCallback(() => setIsNotesPanelOpen(false), [])
  const clearPostCallContext = useCallback(() => setPostCallContext(null), [])

  // Wrapped accept/reject that broadcast to other tabs
  const handleAcceptIncoming = useCallback(() => {
    device.acceptIncoming()
    channelRef.current?.postMessage({ type: 'inbound-accepted' })
  }, [device.acceptIncoming])

  const handleRejectIncoming = useCallback(() => {
    device.rejectIncoming()
    channelRef.current?.postMessage({ type: 'inbound-rejected' })
  }, [device.rejectIncoming])

  return (
    <VoIPContext.Provider value={{
      ...device,
      isCallBarMinimized,
      toggleCallBarMinimize: () => setIsCallBarMinimized(prev => !prev),
      isNotesPanelOpen,
      openNotesPanel,
      closeNotesPanel,
      activeCallMeta,
      setActiveCallMeta,
      postCallContext,
      clearPostCallContext,
    }}>
      {children}

      {/* Incoming call popup — hidden if another tab already handled it */}
      {device.incomingCall && device.status === 'ringing-inbound' && !inboundHandledByOtherTab && (
        <IncomingCallOverlay
          call={device.incomingCall}
          onAccept={handleAcceptIncoming}
          onReject={handleRejectIncoming}
        />
      )}

      {/* Active call bar (bottom of screen) */}
      <ActiveCallBar
        status={device.status}
        duration={device.duration}
        isMuted={device.isMuted}
        onToggleMute={device.toggleMute}
        onHangup={device.disconnect}
        callNumber={device.activeCallNumber}
        isMinimized={isCallBarMinimized}
        onToggleMinimize={() => setIsCallBarMinimized(prev => !prev)}
        connectionUnstable={device.connectionUnstable}
        onOpenNotes={openNotesPanel}
        isNotesPanelOpen={isNotesPanelOpen}
      />

      {/* Slide-out call notes panel */}
      <CallNotesPanel
        isOpen={isNotesPanelOpen}
        onClose={closeNotesPanel}
        callId={activeCallMeta?.callId ?? null}
        leadId={activeCallMeta?.leadId ?? null}
        leadName={activeCallMeta?.leadName ?? null}
      />

      {/* Post-call modal — shown when call ends on non-VoIP page */}
      {showPostCallModal && (
        <PostCallModal
          callOutcome={device.callOutcome}
          callId={activeCallMeta?.callId ?? null}
          duration={device.duration}
          contactName={activeCallMeta?.leadName ?? null}
          callerNumber={callerNumberRef.current || device.activeCallNumber}
          isUnknownInbound={!!postCallContext}
          onClose={() => { setShowPostCallModal(false); setPostCallContext(null) }}
        />
      )}
    </VoIPContext.Provider>
  )
}

export function useVoIP(): VoIPContextValue {
  const ctx = useContext(VoIPContext)
  if (!ctx) {
    throw new Error('useVoIP must be used within a VoIPProvider')
  }
  return ctx
}
